import React from 'react';
import { Box, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { StrapiCustomerRow } from '../../../../../api/strapiCrm';
import { CustomerSearchAutocomplete } from '../../../../../components/CustomerSearchAutocomplete';
import {
  QUOTATION_RECURRING_TYPE_OPTIONS,
  type QuotationRecurringTypeValue,
} from '../../quotationFormConstants';
import {
  quotationFormContractHeaderFromValues,
  type QuotationFormGroupRow,
  type QuotationFormGroupSampleSubtotal,
  type QuotationFormGroupOtherCostSubtotal,
} from '../../quotationFormGroups';
import type { QuotationFormOtherCostRow, QuotationFormSampleRow, QuotationFormValues } from '../../quotationFormTypes';
import {
  QUOTATION_TRIWULAN_RECURRING_INTERVAL,
  quotationFormGroupTotalRecurringCount,
} from '../../quotationRecurring';
import { QuotationFormSamplesTabScreen } from '../Samples';
import { QuotationFormGroupRecurringSummary } from './QuotationFormGroupRecurringSummary';

type Props = {
  jwt: string | null;
  groupIndex: number;
  group: QuotationFormGroupRow;
  is_contract: boolean;
  is_consultant_quote: boolean;
  contract_period_start_date: string;
  contract_period_end_date: string;
  contractPeriodStartDateError?: string;
  contractPeriodEndDateError?: string;
  onContractPeriodChange: (
    patch: Partial<
      Pick<QuotationFormValues, 'contract_period_start_date' | 'contract_period_end_date'>
    >,
  ) => void;
  disabled: boolean;
  readOnly?: boolean;
  showFinancialSummary: boolean;
  canRemoveGroup: boolean;
  onGroupChange: (group: QuotationFormGroupRow) => void;
  onRemoveGroup: () => void;
  customerError?: string;
  recurringTypeError?: string;
  discount_amount: string;
  discount_is_percentage: boolean;
  discount_percentage: string;
  is_ppn: boolean;
  onFinancialPatch: (
    patch: Partial<
      Pick<
        QuotationFormValues,
        'discount_amount' | 'discount_is_percentage' | 'discount_percentage' | 'is_ppn'
      >
    >,
  ) => void;
  financialSampleRows?: QuotationFormSampleRow[];
  financialSampleRecurringCounts?: number[];
  groupSampleSubtotals?: QuotationFormGroupSampleSubtotal[];
  groupOtherCostSubtotals?: QuotationFormGroupOtherCostSubtotal[];
  allOtherCostRowsForGrandTotal?: QuotationFormOtherCostRow[];
  allOtherCostRecurringCounts?: number[];
};

/** Satu tab Sampel (N): field group + tabel sampel seperti tab flat. */
export function QuotationFormSampleGroupTabScreen({
  jwt,
  groupIndex,
  group,
  is_contract,
  is_consultant_quote,
  contract_period_start_date,
  contract_period_end_date,
  contractPeriodStartDateError,
  contractPeriodEndDateError,
  onContractPeriodChange,
  disabled,
  readOnly = false,
  showFinancialSummary,
  canRemoveGroup,
  onGroupChange,
  onRemoveGroup,
  customerError,
  recurringTypeError,
  discount_amount,
  discount_is_percentage,
  discount_percentage,
  is_ppn,
  onFinancialPatch,
  financialSampleRows,
  financialSampleRecurringCounts,
  groupSampleSubtotals,
  groupOtherCostSubtotals,
  allOtherCostRowsForGrandTotal,
  allOtherCostRecurringCounts,
}: Props) {
  const label = groupIndex + 1;
  const contractHeader = quotationFormContractHeaderFromValues({
    contract_period_start_date,
    contract_period_end_date,
  });
  const groupRecurringCount = is_contract
    ? (quotationFormGroupTotalRecurringCount(group, contractHeader) ?? 1)
    : 1;

  const patchGroupFinancial = (
    patch: Partial<
      Pick<
        QuotationFormValues,
        'discount_amount' | 'discount_is_percentage' | 'discount_percentage' | 'is_ppn' | 'otherCostRows'
      >
    >,
  ) => {
    if ('otherCostRows' in patch && patch.otherCostRows) {
      onGroupChange({ ...group, otherCostRows: patch.otherCostRows });
      return;
    }
    onFinancialPatch(patch);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: { xs: '100%', md: '70%' } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          Sampel ({label})
        </Typography>
        {canRemoveGroup && !readOnly ? (
          <Tooltip title="Hapus group sampel ini">
            <IconButton
              size="small"
              color="error"
              onClick={onRemoveGroup}
              disabled={disabled}
              aria-label={`Hapus group sampel ${label}`}
              data-testid={`quotation-form-remove-sample-group-${label}`}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>

      {is_consultant_quote ? (
        <CustomerSearchAutocomplete
          jwt={jwt}
          value={group.customer}
          onChange={(customer) => onGroupChange({ ...group, customer })}
          disabled={disabled || readOnly}
          error={customerError}
          required
          inputTestId={`quotation-form-group-${label}-customer`}
        />
      ) : null}

      {is_contract ? (
        <>
          <TextField
            select
            label="Jenis perulangan"
            required
            fullWidth
            value={group.recurring_type}
            onChange={(e) => {
              const recurring_type = e.target.value as QuotationRecurringTypeValue | '';
              onGroupChange({
                ...group,
                recurring_type,
                recurring_interval:
                  recurring_type === 'triwulan'
                    ? String(QUOTATION_TRIWULAN_RECURRING_INTERVAL)
                    : group.recurring_interval,
              });
            }}
            disabled={disabled || readOnly}
            error={Boolean(recurringTypeError)}
            helperText={recurringTypeError ?? 'Frekuensi pengujian dalam periode kontrak.'}
            SelectProps={{
              displayEmpty: true,
              SelectDisplayProps: {
                'data-testid': `quotation-form-group-${label}-recurring-type-trigger`,
              } as unknown as React.HTMLAttributes<HTMLDivElement>,
            }}
            inputProps={{ 'data-testid': `quotation-form-group-${label}-recurring-type` }}
          >
            <MenuItem value="" disabled>
              Pilih jenis perulangan
            </MenuItem>
            {QUOTATION_RECURRING_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={`Setiap x dalam 1 ${
                QUOTATION_RECURRING_TYPE_OPTIONS.find((o) => o.value === group.recurring_type)?.label ??
                'jenis perulangan'
              }`}
              type="number"
              fullWidth
              inputProps={{ min: 1, 'data-testid': `quotation-form-group-${label}-recurring-interval` }}
              value={group.recurring_interval}
              onChange={(e) => onGroupChange({ ...group, recurring_interval: e.target.value })}
              disabled={disabled || readOnly || group.recurring_type === 'triwulan'}
              helperText="Isi angka x (default 1). Contoh: 2 dan jenis Mingguan = setiap 2 dalam 1 Mingguan."
            />
            <TextField
              label="Penyesuaian jumlah perulangan"
              type="number"
              fullWidth
              inputProps={{ min: 1, 'data-testid': `quotation-form-group-${label}-recurring-count-override` }}
              value={group.recurring_count_override}
              onChange={(e) => onGroupChange({ ...group, recurring_count_override: e.target.value })}
              disabled={disabled || readOnly}
              helperText="Kosongkan untuk hitung otomatis dari periode kontrak."
            />
          </Stack>

          <TextField
            label="Tanggal mulai perulangan (grup)"
            type="datetime-local"
            fullWidth
            value={group.recurring_start_date}
            onChange={(e) => onGroupChange({ ...group, recurring_start_date: e.target.value })}
            disabled={disabled || readOnly}
            helperText="Kosongkan untuk memakai tanggal mulai kontrak di tab Detail."
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': `quotation-form-group-${label}-recurring-start` }}
          />

          <QuotationFormGroupRecurringSummary group={group} contractHeader={contractHeader} />
        </>
      ) : null}

      <QuotationFormSamplesTabScreen
        disabled={disabled}
        readOnly={readOnly}
        sampleRows={group.sampleRows}
        onSampleRowsChange={(sampleRows) => onGroupChange({ ...group, sampleRows })}
        discount_amount={discount_amount}
        discount_is_percentage={discount_is_percentage}
        discount_percentage={discount_percentage}
        is_ppn={is_ppn}
        otherCostRows={group.otherCostRows}
        onFinancialPatch={patchGroupFinancial}
        hideFinancialSummary={!showFinancialSummary}
        financialSampleRows={financialSampleRows}
        financialSampleRecurringCounts={financialSampleRecurringCounts}
        groupSampleSubtotals={groupSampleSubtotals}
        groupOtherCostSubtotals={groupOtherCostSubtotals}
        allOtherCostRowsForGrandTotal={allOtherCostRowsForGrandTotal}
        allOtherCostRecurringCounts={allOtherCostRecurringCounts}
        contractRecurringCount={is_contract ? groupRecurringCount : undefined}
      />
      </Stack>

      <Box
        sx={{
          width: { xs: '100%', md: '30%' },
          flex: { md: '0 0 30%' },
          position: { md: 'sticky' },
          top: 0,
          alignSelf: { md: 'flex-start' },
          zIndex: 1,
          bgcolor: 'background.paper',
          boxSizing: 'border-box',
          pl: { md: 2 },
          pt: { xs: 2, md: 0 },
          borderLeft: { md: 1 },
          borderTop: { xs: 1, md: 0 },
          borderColor: 'divider',
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle2" fontWeight={600}>
            Periode kontrak
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tanggal header quotation. Sama untuk setiap sampel.
          </Typography>
          <TextField
            label="Tanggal mulai kontrak"
            type="datetime-local"
            required={is_contract}
            fullWidth
            value={contract_period_start_date}
            onChange={(e) => onContractPeriodChange({ contract_period_start_date: e.target.value })}
            disabled={disabled || readOnly}
            error={Boolean(contractPeriodStartDateError)}
            helperText={contractPeriodStartDateError}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'quotation-form-sample-contract-period-start' }}
          />
          <TextField
            label="Tanggal kontrak selesai"
            type="datetime-local"
            required={is_contract}
            fullWidth
            value={contract_period_end_date}
            onChange={(e) => onContractPeriodChange({ contract_period_end_date: e.target.value })}
            disabled={disabled || readOnly}
            error={Boolean(contractPeriodEndDateError)}
            helperText={
              contractPeriodEndDateError ??
              'Durasi kontrak dihitung dari selisih tanggal mulai dan tanggal selesai.'
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'quotation-form-sample-contract-period-end' }}
          />
        </Stack>
      </Box>
    </Box>
  );
}
